import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Crown, Sparkles, ArrowLeft, LogIn } from "lucide-react";
import Navbar from "../Landing/Navbar";
import Footer from "../Components/Footer";

const PLANS = [
  {
    id: "premium_6months",
    name: "6 Months Premium",
    duration: "6 Months",
    originalPrice: 7999,
    offerPrice: 4999,
    badge: null,
  },
  {
    id: "premium_annual",
    name: "1 Year Premium",
    duration: "1 Year",
    originalPrice: 11999,
    offerPrice: 6999,
    badge: "Most Popular",
  },
];

const FEATURES = [
  { feature: "Browse matches",       free: true,           premium: true },
  { feature: "View profiles",        free: true,           premium: true },
  { feature: "Send interests",       free: "5 only",       premium: true },
  { feature: "Receive interests",    free: true,           premium: true },
  { feature: "View contact number",  free: false,          premium: true },
  { feature: "See who viewed you",   free: "Count only",   premium: true },
  { feature: "View all photos",      free: "1st only",     premium: true },
  { feature: "Advanced filters",     free: false,          premium: true },
  { feature: "Priority listing",     free: false,          premium: true },
  { feature: "Premium badge",        free: false,          premium: true },
];

const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } };

export default function Pricing() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="page-container pt-6 pb-app-nav sm:pt-10 lg:pt-14">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-600 hover:text-brand-700"
        >
          <ArrowLeft size={14} /> Back to home
        </Link>

        {/* Hero */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="mt-6 text-center"
        >
          <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-brand-gradient text-white shadow-glow">
            <Crown size={28} />
          </div>
          <h1 className="mt-5 font-display text-3xl font-bold text-ink-800 sm:text-4xl">
            Simple, honest pricing
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-[14.5px] leading-7 text-ink-500">
            Unlock the full Karanki Matrimony experience with Premium — no hidden fees, no auto-renewal.
          </p>
        </motion.div>

        {/* Plans */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          className="mt-10 grid gap-4 sm:grid-cols-2 sm:max-w-2xl sm:mx-auto"
        >
          {PLANS.map((plan) => (
            <motion.div
              key={plan.id}
              variants={fadeUp}
              className={`relative overflow-hidden rounded-3xl border-2 p-6 text-left ${
                plan.badge
                  ? "border-brand-600 bg-brand-50/40 shadow-elevated"
                  : "border-sand-200 bg-white"
              }`}
            >
              {plan.badge && (
                <span className="absolute right-4 top-4 rounded-full bg-brand-gradient px-2.5 py-0.5 text-[10px] font-bold uppercase text-white shadow-soft">
                  {plan.badge}
                </span>
              )}
              <p className="text-[12px] font-bold uppercase tracking-wider text-ink-400">
                {plan.duration}
              </p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-3xl font-bold text-ink-800">
                  ₹{plan.offerPrice.toLocaleString("en-IN")}
                </span>
                <span className="text-[13px] text-ink-400 line-through">
                  ₹{plan.originalPrice.toLocaleString("en-IN")}
                </span>
              </div>
              <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                <Sparkles size={10} />
                Save ₹{(plan.originalPrice - plan.offerPrice).toLocaleString("en-IN")}
              </p>
              <p className="mt-3 text-[12.5px] text-ink-500">
                One-time payment · No auto-renewal
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Login CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center"
        >
          <Link
            to="/login"
            className="inline-flex items-center gap-2 rounded-2xl bg-brand-gradient px-7 py-3.5 text-[14.5px] font-bold text-white shadow-soft hover:shadow-elevated transition"
          >
            <LogIn size={16} /> Login to upgrade
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-2xl border border-sand-200 bg-white px-7 py-3.5 text-[14px] font-semibold text-ink-700 hover:border-brand-300 transition"
          >
            New here? Register free
          </Link>
        </motion.div>

        {/* Feature comparison */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mt-14 sm:max-w-2xl sm:mx-auto"
        >
          <h2 className="font-display text-xl font-bold text-ink-800 text-center">
            Free vs Premium
          </h2>
          <p className="mt-1 text-center text-[13px] text-ink-400">
            See everything you unlock with Premium
          </p>

          <div className="mt-5 overflow-hidden rounded-3xl border border-sand-200">
            <div className="grid grid-cols-[1fr_70px_70px] items-center border-b border-sand-200 bg-sand-50 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-ink-400">
              <span>Feature</span>
              <span className="text-center">Free</span>
              <span className="text-center text-brand-600">Premium</span>
            </div>
            {FEATURES.map(({ feature, free, premium }, idx) => (
              <div
                key={feature}
                className={`grid grid-cols-[1fr_70px_70px] items-center px-4 py-3 ${
                  idx % 2 === 0 ? "bg-white" : "bg-sand-50/50"
                }`}
              >
                <span className="text-[13px] font-medium text-ink-700">{feature}</span>
                <span className="flex justify-center">
                  {free === true ? (
                    <Check size={16} className="text-emerald-500" />
                  ) : free === false ? (
                    <X size={16} className="text-rose-400" />
                  ) : (
                    <span className="text-[10.5px] font-semibold text-amber-600">{free}</span>
                  )}
                </span>
                <span className="flex justify-center">
                  {premium === true ? (
                    <Check size={16} className="text-emerald-500" />
                  ) : (
                    <X size={16} className="text-rose-400" />
                  )}
                </span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Bottom CTA */}
        <div className="mt-10 flex justify-center pb-4">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 rounded-2xl bg-brand-gradient px-8 py-4 text-[15px] font-bold text-white shadow-elevated hover:shadow-glow transition"
          >
            <Crown size={17} /> Get Premium now
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
